import { Component, DestroyRef, inject, OnDestroy, OnInit } from '@angular/core';
import { HttpServiceService } from '../../services/http-service.service';
import { LoadingService } from '../../services/loading.service';
import { LocalStorageService } from '../../services/local-storage.service';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { NgxToastrService } from '../../_services/ngx-toastr/ngx-toastr.service';
import { Select2Data } from 'ng-select2-component';
import { Subject, debounceTime, tap, switchMap, Subscription, interval, takeWhile } from 'rxjs';
import { NgIf, NgFor, NgClass, DatePipe, CurrencyPipe } from '@angular/common';
import { NgSelectModule } from '@ng-select/ng-select';
import { BreadcrumbComponent } from '../../elements/breadcrumb/breadcrumb.component';
import { PaginationComponent } from '../../elements/pagination/pagination.component';
import { USER_PERMISSIONS } from '../../app.constants';
import {
  LeaseManagerReportRow,
  LeaseReportRow,
  LeaseReportSummary,
  ReportJobStatusResponse
} from '../../models/reports.model';

@Component({
  selector: 'app-lease-reports',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    PaginationComponent,
    BreadcrumbComponent,
    NgSelectModule,
    NgIf,
    NgFor,
    NgClass,
    DatePipe,
    CurrencyPipe
  ],
  templateUrl: './lease-reports.component.html',
  styleUrl: './lease-reports.component.css'
})
export class LeaseReportsComponent implements OnInit, OnDestroy {
  breadcrumbList = {
    breadcrumb_path: 'Reports',
    currentURL: 'Leases',
    breadcrumb_url: '/admin/reports/leases'
  };

  page: number = 1;
  totalRows: number = 50;
  totalPage: number = 0;
  pageCount: number = 1;

  reportData: LeaseReportRow[] = [];
  summaryData: LeaseReportSummary | null = null;

  activeTab: 'leases' | 'managers' = 'leases';
  managerReportData: LeaseManagerReportRow[] = [];
  private managerReportLoaded = false;

  filterForm!: FormGroup;
  disableClear: boolean = false;

  managersList: any[] = [];
  managersItems: Select2Data = [];
  searchManager$ = new Subject<string>();
  private managerPage = 1;
  private managerPageSize = 20;
  private hasMoreManagers = true;

  isDownloading: boolean = false;
  downloadProgress: string = '';
  activeDownloadType: 'pdf' | 'csv' | 'email' | 'whatsapp' | null = null;

  showShareModal: boolean = false;
  shareType: 'email' | 'whatsapp' = 'email';
  shareForm!: FormGroup;

  reportTypeOptions = [
    { value: 'pdf', label: 'PDF' },
    { value: 'csv', label: 'CSV' }
  ];

  private httpService = inject(HttpServiceService);
  private authService = inject(LocalStorageService);
  loadingService = inject(LoadingService);

  private reportSubscription?: Subscription;
  private managerSearchSubscription?: Subscription;

  constructor(
    private fb: FormBuilder,
    private alertService: NgxToastrService
  ) {
    this.initFilterForm();
    this.initShareForm();
  }

  ngOnInit(): void {
    this.setupManagerSearch();
    this.loadReport();
  }

  private initFilterForm(): void {
    this.filterForm = this.fb.group({
      managed_by: [[]],
      lease_end_start: [''],
      lease_end_end: [{ value: '', disabled: true }]
    });

    this.filterForm.get('lease_end_start')!.valueChanges.subscribe((value) => {
      const endCtrl = this.filterForm.get('lease_end_end')!;
      if (value) {
        endCtrl.enable();
      } else {
        endCtrl.disable();
        endCtrl.reset();
      }
    });
  }

  private initShareForm(): void {
    this.shareForm = this.fb.group({
      selectedUser: [null],
      file_type: ['pdf'],
      first_name: [''],
      last_name: [''],
      email: [''],
      country_code: [''],
      whatsapp: ['']
    });
  }

  private setupManagerSearch(): void {
    this.managerSearchSubscription = this.searchManager$
      .pipe(
        debounceTime(300),
        tap(() => {
          this.managerPage = 1;
          this.hasMoreManagers = true;
        }),
        switchMap((term) => {
          const search = term ? term : '';
          return this.httpService.getPropertyManagers(this.managerPage, this.managerPageSize, search);
        })
      )
      .subscribe((response) => {
        this.managersList = response.data?.users ?? [];
        this.hasMoreManagers = this.managersList.length < (response.data?.total ?? 0);
        this.managersItems = this.managersList.map((m: any) => ({
          value: m.uid,
          label: `${m.first_name} ${m.last_name}`,
          data: {
            first_name: m.first_name,
            last_name: m.last_name,
            email: m.email,
            phone: m.phone,
            country_code: m.country_code
          }
        }));
      });
    this.searchManager$.next('');
  }

  onManagerScrollEnd(): void {
    if (!this.hasMoreManagers) return;
    this.managerPage++;
    this.httpService.getPropertyManagers(this.managerPage, this.managerPageSize)
      .subscribe((resp) => {
        const more = resp.data?.users ?? [];
        this.managersList = [...this.managersList, ...more];
        this.hasMoreManagers = this.managersList.length < (resp.data?.total ?? 0);
        this.managersItems = this.managersList.map((m: any) => ({
          value: m.uid,
          label: `${m.first_name} ${m.last_name}`,
          data: {
            first_name: m.first_name,
            last_name: m.last_name,
            email: m.email,
            phone: m.phone,
            country_code: m.country_code
          }
        }));
      });
  }

  onShareUserChange(selectedItem: any): void {
    if (selectedItem && selectedItem.data) {
      this.shareForm.patchValue({
        first_name: selectedItem.data.first_name || '',
        last_name: selectedItem.data.last_name || ''
      });
      if (this.shareType === 'email') {
        this.shareForm.patchValue({ email: selectedItem.data.email || '' });
      } else {
        this.shareForm.patchValue({
          country_code: selectedItem.data.country_code || '',
          whatsapp: selectedItem.data.phone || ''
        });
      }
    }
  }

  /** The Manager filter is multi-select; normalise whatever ng-select hands back to a uid list. */
  private managerIdsFromValue(value: any): string[] {
    const list = Array.isArray(value) ? value : value ? [value] : [];
    return list
      .map((v: any) => (v && typeof v === 'object' ? v.value : v))
      .filter((v: any) => !!v);
  }

  private buildFilterQuery(): string {
    let query = '';
    const v = this.filterForm.getRawValue();
    const managerIds = this.managerIdsFromValue(v.managed_by);
    if (managerIds.length) query += `&managed_by=${managerIds.join(',')}`;
    if (v.lease_end_start) query += `&lease_end_start=${v.lease_end_start}`;
    if (v.lease_end_end) query += `&lease_end_end=${v.lease_end_end}`;
    return query;
  }

  loadReport(): void {
    this.reportSubscription?.unsubscribe();
    const query = this.buildFilterQuery();
    this.reportSubscription = this.httpService
      .getLeaseReportAll(this.page, this.totalRows, query)
      .subscribe({
        next: (response) => {
          if (response.success) {
            this.reportData = response.data.rows ?? [];
            this.summaryData = response.data.summary ?? null;
            this.totalPage = response.data.total ?? 0;
            this.pageCount = response.data.pageCount ?? 1;
          }
        },
        error: () => {
          this.alertService.error('Failed to load lease report', 'toast-top-right');
        }
      });
  }

  setTab(tab: 'leases' | 'managers'): void {
    if (this.activeTab === tab) return;
    this.activeTab = tab;
    if (tab === 'managers' && !this.managerReportLoaded) {
      this.loadManagerReport();
    }
  }

  loadManagerReport(): void {
    this.loadingService.show();
    this.httpService.getLeaseReportByManager().subscribe({
      next: (response) => {
        this.loadingService.hide();
        if (response.success) {
          this.managerReportData = response.data.rows ?? [];
          this.managerReportLoaded = true;
        }
      },
      error: () => {
        this.loadingService.hide();
        this.alertService.error('Failed to load manager lease report', 'toast-top-right');
      }
    });
  }

  onFilterChange(): void {
    this.disableClear = true;
    this.page = 1;
    this.loadReport();
  }

  resetFilters(): void {
    this.filterForm.reset({ managed_by: [], lease_end_start: '', lease_end_end: '' });
    this.disableClear = false;
    this.page = 1;
    this.loadReport();
  }

  pageChange(page: number): void {
    if (this.page === page) return;
    this.page = page;
    this.loadReport();
  }

  applyExpiringFilter(days: number): void {
    const today = new Date();
    const future = new Date(today.getTime() + days * 24 * 60 * 60 * 1000);
    const toDateStr = (d: Date) => d.toISOString().split('T')[0];
    this.filterForm.patchValue({
      lease_end_start: toDateStr(today),
      lease_end_end: toDateStr(future)
    });
    // Enable the end date control since start is now set
    this.filterForm.get('lease_end_end')!.enable();
    this.disableClear = true;
    this.page = 1;
    this.loadReport();
  }

  downloadReport(fileType: 'pdf' | 'csv'): void {
    if (this.isDownloading) return;
    this.isDownloading = true;
    this.activeDownloadType = fileType;
    this.downloadProgress = 'Initiating...';
    const query = this.buildFilterQuery();
    this.httpService.downloadLeaseReportAll(fileType, query).subscribe({
      next: (response) => {
        if (response.success && response.data?.job_id) {
          this.pollJobStatus(response.data.job_id);
        } else {
          this.resetDownloadState();
          this.alertService.error('Failed to initiate download', 'toast-top-right');
        }
      },
      error: () => {
        this.resetDownloadState();
        this.alertService.error('Failed to initiate download', 'toast-top-right');
      }
    });
  }

  private resetDownloadState(): void {
    this.isDownloading = false;
    this.downloadProgress = '';
    this.activeDownloadType = null;
  }

  private pollJobStatus(jobId: string): void {
    const maxPollingTime = 30000;
    const pollingInterval = 1000;
    const startTime = Date.now();
    let pollCount = 0;
    this.checkJobStatus(jobId);
    const poll$ = interval(pollingInterval).pipe(
      takeWhile(() => (Date.now() - startTime) < maxPollingTime && this.isDownloading)
    );
    poll$.subscribe({
      next: () => {
        pollCount++;
        this.downloadProgress = `Checking... (${pollCount})`;
        this.checkJobStatus(jobId);
      },
      complete: () => {
        if (this.isDownloading) {
          this.resetDownloadState();
          this.alertService.error('Report generation timed out. Please try again.', 'toast-top-right');
        }
      }
    });
  }

  private checkJobStatus(jobId: string): void {
    this.httpService.getReportJobStatus(jobId).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          const jobData: ReportJobStatusResponse = response.data;
          if (jobData.status === 'completed' && jobData.file_url) {
            this.resetDownloadState();
            this.alertService.success('Report generated successfully!', 'toast-top-right');
            window.open(jobData.file_url, '_blank');
          } else if (jobData.status === 'failed') {
            this.resetDownloadState();
            this.alertService.error(jobData.error_message || 'Report generation failed', 'toast-top-right');
          }
        }
      },
      error: () => {}
    });
  }

  openShareModal(type: 'email' | 'whatsapp'): void {
    this.shareType = type;
    this.shareForm.reset({ file_type: 'pdf' });
    this.showShareModal = true;
  }

  closeShareModal(): void {
    this.showShareModal = false;
    this.shareForm.reset({ file_type: 'pdf' });
  }

  submitShare(): void {
    const formValue = this.shareForm.getRawValue();
    const shareValue = this.shareType === 'email' ? formValue.email : formValue.whatsapp;
    if (!shareValue || shareValue.trim() === '') {
      this.alertService.error(
        `Please enter ${this.shareType === 'email' ? 'an email address' : 'a WhatsApp number'}`,
        'toast-top-right'
      );
      return;
    }
    this.closeShareModal();
    this.isDownloading = true;
    this.activeDownloadType = this.shareType;
    this.downloadProgress = 'Sending...';
    const query = this.buildFilterQuery();
    const shareData: any = {
      share_type: this.shareType,
      first_name: formValue.first_name?.trim() || '',
      last_name: formValue.last_name?.trim() || ''
    };
    if (this.shareType === 'email') {
      shareData.email = shareValue.trim();
    } else {
      shareData.country_code = formValue.country_code?.trim() || '';
      shareData.whatsapp = shareValue.trim();
    }
    const fileType = formValue.file_type || 'pdf';
    this.httpService.downloadLeaseReportAll(fileType, query, shareData).subscribe({
      next: (response) => {
        if (response.success && response.data?.job_id) {
          this.pollJobStatusForShare(response.data.job_id);
        } else {
          this.resetDownloadState();
          this.alertService.error('Failed to initiate share', 'toast-top-right');
        }
      },
      error: () => {
        this.resetDownloadState();
        this.alertService.error('Failed to initiate share', 'toast-top-right');
      }
    });
  }

  private pollJobStatusForShare(jobId: string): void {
    const maxPollingTime = 30000;
    const pollingInterval = 1000;
    const startTime = Date.now();
    let pollCount = 0;
    this.checkJobStatusForShare(jobId);
    const poll$ = interval(pollingInterval).pipe(
      takeWhile(() => (Date.now() - startTime) < maxPollingTime && this.isDownloading)
    );
    poll$.subscribe({
      next: () => {
        pollCount++;
        this.downloadProgress = `Processing... (${pollCount})`;
        this.checkJobStatusForShare(jobId);
      },
      complete: () => {
        if (this.isDownloading) {
          this.resetDownloadState();
          this.alertService.error('Share request timed out. Please try again.', 'toast-top-right');
        }
      }
    });
  }

  private checkJobStatusForShare(jobId: string): void {
    this.httpService.getReportJobStatus(jobId).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          const jobData: ReportJobStatusResponse = response.data;
          if (jobData.status === 'completed') {
            this.resetDownloadState();
            this.alertService.success(`Report shared via ${this.shareType} successfully!`, 'toast-top-right');
          } else if (jobData.status === 'failed') {
            this.resetDownloadState();
            this.alertService.error(jobData.error_message || 'Failed to share report', 'toast-top-right');
          }
        }
      },
      error: () => {}
    });
  }

  get checkDownloadPermission(): boolean {
    return this.authService.checkPermission(USER_PERMISSIONS.REPORTS.REPORTS_DOWNLOAD);
  }

  ngOnDestroy(): void {
    this.reportSubscription?.unsubscribe();
    this.managerSearchSubscription?.unsubscribe();
  }
}
